import React, { useState } from "react";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import MobileLayout from "../MobileLayout";
import logo from "../asset/logo/LOGO_ORANGE.svg";
import { sendConsent } from "./consentService";

// Before the first breef: the participant reads how their data is used and accepts or declines. Declining means
// they can't take part (told before they confirm); both answers are recorded. Same layout as the privacy page.
const POINTS = ["who", "winner", "howLong", "rights"];

const primary = { backgroundColor: "#EB6608", color: "#FFFFFF", width: "100%", textTransform: "none" as const, height: "3rem", borderRadius: "10px" };
const secondary = { color: "#EB6608", borderColor: "#EB6608", width: "100%", textTransform: "none" as const, height: "3rem", borderRadius: "10px" };

export const ConsentStep = ({ version, insight, onAccepted }: { version: string; insight?: { smallGroups: boolean; persons: boolean }; onAccepted: () => void }) => {
  const [t] = useTranslation("global");
  const [step, setStep] = useState<"ask" | "confirmDecline" | "declined">("ask");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const answer = async (value: "accepted" | "declined") => {
    setBusy(true);
    setError(false);
    try {
      await sendConsent(value, version);
      if (value === "accepted") onAccepted();
      else setStep("declined");
    } catch {
      setError(true);
    }
    setBusy(false);
  };

  return (
    <MobileLayout scrollable>
      <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0 1rem" }}>
        <img height={60} src={logo} alt="Breefer" />
      </div>
      <div style={{ padding: "0 1.5rem 2rem", fontFamily: "Roboto, Arial, sans-serif", lineHeight: 1.5 }}>
        {step === "ask" && (
          <>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 400 }}>{t("consent.title")}</h1>
            <p>{t("consent.intro")}</p>
            {POINTS.map((key) => (
              <React.Fragment key={key}>
                <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#EB6608", marginTop: "1.2rem" }}>{t(`privacy.${key}.title`)}</h2>
                <p>{t(`privacy.${key}.text`)}</p>
                {/* The employer turned extra insight on for this breef (its own choice): the participant is told before answering */}
                {key === "who" && (insight?.persons || insight?.smallGroups) && (
                  <div style={{ border: "2px solid #EB6608", borderRadius: "10px", padding: "0.8rem 1rem" }} data-testid="consent-insight">
                    <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#EB6608", margin: "0 0 0.4rem" }}>{t("consent.insightTitle")}</h2>
                    <p style={{ margin: "0 0 0.6rem" }}>{t(insight.persons ? "consent.insightPersons" : "consent.insightSmallGroups")}</p>
                    <p style={{ margin: 0 }}>{t("consent.insightDuty")}</p>
                  </div>
                )}
              </React.Fragment>
            ))}
            <p>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#EB6608", fontWeight: 700 }}>
                {t("consent.readAll")}
              </a>
            </p>
            <p>{t("consent.voluntary")}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem", marginTop: "1.5rem" }}>
              <Button variant="contained" style={primary} disabled={busy} onClick={() => answer("accepted")}>
                {t("consent.accept")}
              </Button>
              <Button variant="outlined" style={secondary} disabled={busy} onClick={() => setStep("confirmDecline")}>
                {t("consent.decline")}
              </Button>
            </div>
          </>
        )}
        {step === "confirmDecline" && (
          <>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 400 }}>{t("consent.confirmTitle")}</h1>
            <p>{t("consent.confirmText")}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem", marginTop: "1.5rem" }}>
              <Button variant="contained" style={primary} disabled={busy} onClick={() => answer("declined")}>
                {t("consent.confirmDecline")}
              </Button>
              <Button variant="outlined" style={secondary} disabled={busy} onClick={() => setStep("ask")}>
                {t("consent.back")}
              </Button>
            </div>
          </>
        )}
        {step === "declined" && (
          <>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 400 }}>{t("consent.declinedTitle")}</h1>
            <p>{t("consent.declinedText")}</p>
            <Button variant="outlined" style={secondary} onClick={() => setStep("ask")}>
              {t("consent.changeMind")}
            </Button>
          </>
        )}
        {error && <p style={{ color: "#D32F2F" }}>{t("consent.error")}</p>}
      </div>
    </MobileLayout>
  );
};
